<template>
  <div
    class="autocomplete cn-input"
    :class="[
      {
        'autocomplete--disabled': disabled,
      },
    ]"
  >
    <div class="autocomplete__wrapper w-full">
      <UiIcon v-if="!loading" class="autocomplete__icon" name="search" />
      <UiSpinner v-else class="autocomplete__loader" />
      <span
        v-if="!valueLocal"
        class="autocomplete__placeholder pointer-events-none"
      >
        {{ placeholder }}
      </span>
      <PVAutoComplete
        ref="autocomplete"
        v-model="valueLocal"
        class="w-full"
        append-to="self"
        :class="[
          {
            'pointer-events-none cursor-not-allowed ': disabled || loading,
            disabled: disabled || loading,
          },
        ]"
        v-bind="$attrs"
        @click="
          () => {
            $emit('click');
          }
        "
        @complete="$emit('complete', $event)"
        @item-select="$emit('item-select', $event)"
        @focus="
          () => {
            $emit('focus');
          }
        "
        @blur="
          () => {
            $emit('blur');
          }
        "
      >
        <template #option="slotProps">
          <slot name="acItem" :item="slotProps.option" />
        </template>
      </PVAutoComplete>
    </div>
    <div
      v-if="!loading && $attrs.suggestions && $attrs.suggestions.length"
      class="autocomplete__count mt-4"
    >
      {{ $t('found') }} <span>{{ $attrs.suggestions.length }}</span>
      {{ countType }}
    </div>
    <div
      v-else-if="
        !loading &&
        $attrs.suggestions !== null &&
        (!$attrs.suggestions || !$attrs.suggestions.length)
      "
      class="autocomplete__count mt-4"
    >
      {{ $t('nothing_found') }}
    </div>
    <span v-if="errors.length" class="block py-1 text-red">
      {{ errors[0] }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { defineProps, withDefaults, defineEmits, computed, ref } from 'vue';
import PVAutoComplete from 'primevue/autocomplete';

interface Props {
  id?: string | null;
  name?: string | null;
  placeholder?: string;
  modelValue?: string;
  errors?: any[];
  block?: boolean;
  disabled?: boolean;
  loading?: boolean;
  countType?: string;
}
interface Emits {
  (e: 'update:modelValue', data: string): void;

  (e: 'click' | 'focus' | 'blur'): void;

  (e: 'complete' | 'item-select', data: any): void;
}

const props = withDefaults(defineProps<Props>(), {
  id: null,
  name: null,
  placeholder: undefined,
  modelValue: '',
  errors: () => [],
  block: false,
  disabled: false,
  loading: false,
  countType: '',
});
const emit = defineEmits<Emits>();
const valueLocal = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit('update:modelValue', newValue);
  },
});
const autocomplete = ref();
</script>
<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<style scoped lang="scss">
.autocomplete {
  --ab-canyon-select-element-color-text-disabled: var(
    --ab-canyon-color-neutral
  );
}
.autocomplete__wrapper {
  position: relative;
}

.autocomplete__icon {
  position: absolute;
  width: 24px;
  height: 24px;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  color: var(--ab-canyon-input-icon-color);
}
.autocomplete__loader {
  position: absolute;
  width: 24px;
  height: 24px;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  padding: 0;
}
.autocomplete__loader :deep(svg) {
  margin: 0;
  width: 100%;
  height: 100%;
}
.autocomplete__placeholder {
  position: absolute;
  left: 0px;
  width: 100%;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  color: var(--ab-canyon-input-placeholder-color);
  padding-left: calc(1.5rem + 24px + 12px);
  font-size: 20px;
  font-weight: 200;
  text-transform: uppercase;
}
.autocomplete__count {
  color: var(var(--ab-canyon-input-text-color));
}
.autocomplete__count span {
  color: var(--ab-canyon-input-text-color);
}
:deep(.p-autocomplete-input) {
  background-color: transparent;
  display: block;
  width: 100%;
  height: 50px;
  background-color: var(--ab-canyon-input-color-bg);
  transition-property: box-shadow;

  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 150ms;
  letter-spacing: 0.04;
  font-size: 20px;
  padding-left: calc(1.5rem + 24px + 12px);
  padding-right: 1.5rem;
  color: var(--ab-canyon-input-text-color);
  border: 1px solid var(--ab-canyon-input-border-color);
  text-transform: uppercase;
  font-weight: 200;
}
:deep(.p-autocomplete-input:focus) {
  border-color: var(--ab-canyon-input-border-color-focus);
  outline: none;
  outline-offset: 0;
  outline-color: var(--ab-canyon-input-outline-color-focus);
  box-shadow: 0px 0px 4px -1px
    color-mix(
      in srgb,
      var(--ab-canyon-input-border-color-focus),
      transparent 20%
    );
}
:deep(.p-autocomplete .p-autocomplete-loader) {
  display: none;
}

:deep(.p-autocomplete-panel) {
  background: var(--ab-canyon-input-color-bg);
  color: var(--ab-canyon-input-text-color);
  border: 1px solid var(--ab-canyon-input-border-color);
  box-shadow: none;
  overflow: scroll;
}
:deep(.p-autocomplete-panel .p-autocomplete-items) {
  padding: 0.5rem 0;
}
:deep(.p-autocomplete-panel .p-autocomplete-items .p-autocomplete-item) {
  margin: 0;
  padding: 0.5rem 1.5rem;
  border: 0 none;
  color: var(--ab-canyon-input-text-color);
  background: transparent;
  transition: box-shadow 0.15s;
  border-radius: 0;
  text-transform: uppercase;
  white-space: unset;
}
:deep(.p-autocomplete-panel .p-autocomplete-items .p-autocomplete-item:hover) {
  color: var(--ab-canyon-color-accent);
  background: var(--ab-canyon-input-color-bg);
}

.autocomplete--disabled {
  .autocomplete__icon,
  .autocomplete__placeholder,
  .autocomplete__count {
    color: var(--ab-canyon-select-element-color-text-disabled);
  }
  .autocomplete__wrapper {
    cursor: not-allowed;
  }
  opacity: 0.7;
}
</style>
